import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

// molecule — DWR choice row: check 16px + title + desc. Pattern .choice-row
// mockup (styles.css righe 845-885). aria-pressed con bg paper-soft + border ink
// + check svg disegnato a mano (path M4 17 L13 26 L27 5).
// near-duplicate of future <Row variant>: emergerà quando in PR 5-6 emergono
// stat-row / bond-row / aspetti-row (composition principles §3.2). Per ora
// molecule dedicato (2 istanze in PR 4: SectionRace + SectionArchetype).
//
// API:
// - title + description: tipici contenuti
// - selected: aria-pressed flag
// - children: slot opzionale per content custom sotto la descrizione (es.
//   input "race custom" in SectionRace, fineprint suggested in SectionArchetype)

interface ChoiceRowProps {
  title: ReactNode;
  description?: ReactNode;
  selected: boolean;
  onClick?: (() => void) | undefined;
  className?: string | undefined;
  children?: ReactNode;
  /** Se true, non renderizza come <button>: usa <div> (per row con input interattivi che
   *  non vogliono propagare il click al toggle). */
  asContainer?: boolean;
}

export function ChoiceRow({
  title,
  description,
  selected,
  onClick,
  className,
  children,
  asContainer = false,
}: ChoiceRowProps) {
  const content = (
    <>
      <CheckBox checked={selected} />
      <div className="flex-1">
        <div className="font-semibold">{title}</div>
        {description ? (
          <div className="text-ink-soft mt-[0.15em] text-[0.88rem]">{description}</div>
        ) : null}
        {children}
      </div>
    </>
  );

  // FI-76: aggiunto `active:bg-paper-soft` per tap/mousedown feedback
  // (oggi ChoiceRow ha solo hover, manca conferma visiva pre-rilascio).
  const baseClasses = cn(
    'border-rule bg-paper-bright flex items-start gap-[0.6rem] border px-[0.7em] py-[0.5em] text-left transition-[background-color,border-color] duration-150 active:bg-paper-soft',
    selected ? 'border-ink bg-paper-soft' : 'hover:border-ink',
    className,
  );

  if (asContainer) {
    // Bug pre-esistente emerso da review FI-76 (2026-05-22): il branch
    // asContainer non aveva onClick → click sulla card "Razza custom" non
    // selezionava. L'unico modo era focusare l'input (`onFocus={selectCustom}`
    // del caller). Ora il click su tutta l'area card seleziona — l'input
    // resta operativo (focus + change → seleziona + nome). selectCustom è
    // idempotente: il doppio dispatch (div.onClick + input.onFocus) non è
    // un problema.
    return (
      <div
        className={cn(baseClasses, onClick && 'cursor-pointer')}
        aria-pressed={selected}
        onClick={onClick}
      >
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(baseClasses, 'cursor-pointer')}
    >
      {content}
    </button>
  );
}

function CheckBox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className="border-ink bg-paper-bright relative mt-[0.2em] inline-block h-4 w-4 flex-shrink-0 border-2"
    >
      {checked ? (
        <svg
          viewBox="0 0 30 30"
          className="pointer-events-none absolute top-1/2 left-1/2 h-[26px] w-[26px] -translate-x-1/2 -translate-y-1/2"
        >
          <path
            d="M4 17 L13 26 L27 5"
            fill="none"
            stroke="currentColor"
            strokeWidth="5.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </span>
  );
}
